// src/components/sections/SocialImpactSection.tsx
import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import clsx from 'clsx';
import { Heart, ExternalLink, Edit2, Award, Globe } from 'lucide-react';
import { Card } from '@dopomogai/ui';
import {Button} from '@dopomogai/ui';
import { AgentHubPageViewMode } from '@dopomogai/types';
import { INNER_CARD_ANIMATE_RESTING, INNER_CARD_HOVER, INNER_CARD_TRANSITION_HOVER } from '../../shared/animationConstants';

// Unified data interface for the component's props
export interface SocialImpactData {
  titleKey: string;
  statementKey: string;
  learnMoreUrl?: string;
  learnMoreButtonKey?: string;
  contribution?: {
    amount: number;
    currency?: string;
    labelKey: string;
    subLabelKey: string;
  };
  spotlightStory?: {
    titleKey: string;
    descriptionKey: string;
    imageUrl?: string;
    linkToStory?: string;
    buttonKey: string;
  };
}

interface SocialImpactSectionProps {
  data: SocialImpactData;
  viewMode: AgentHubPageViewMode;
  onAction: (actionType: string, payload?: any) => void;
  headerBgColor?: string;
  id?: string;
}

export const SocialImpactSection = React.forwardRef<HTMLDivElement, SocialImpactSectionProps>(
  ({ data, viewMode, onAction, headerBgColor, id }, ref) => {
    const { t } = useTranslation('common'); 
    const isOwnerView = viewMode === 'owner_dashboard';

    if (!data) return null;
    
    const hasSpotlightContent = !!(data.contribution || data.spotlightStory);

    return (
      <Card
        ref={ref}
        id={id}
        title={t(data.titleKey)}
        headerBgColor={headerBgColor}
        icon={<Heart size={22} className="text-violet-600" />}
        actionButton={ isOwnerView ? <Button text="Edit" variant="outline" size="sm" icon={<Edit2 size={14} />} onClick={() => onAction('EDIT_SOCIAL_IMPACT', {})} /> : undefined }
      >
        <div className="space-y-10">
          <div className="bg-gradient-to-br from-violet-50 via-fuchsia-50 to-pink-50 p-6 rounded-xl border-2 border-black shadow-[3px_3px_0_0_rgb(17,24,39)]">
            <div className="flex flex-col md:flex-row items-center text-center md:text-left gap-6">
              <div className="p-4 bg-white rounded-full border-2 border-black shrink-0"><Globe size={40} className="text-violet-600" /></div>
              <div>
                <h3 className="text-lg md:text-xl font-bold text-gray-800 mb-2">{t('socialImpactSection.coreCommitmentTitle')}</h3>
                <p className="text-gray-700 leading-relaxed">{t(data.statementKey)}</p>
              </div>
            </div>
          </div>
          
          {hasSpotlightContent && (
            <div>
              <h3 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
                <Award size={28} className="mr-3 text-amber-500" />{t('socialImpactSection.spotlightTitle')}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
                {data.contribution && (
                  <motion.div animate={INNER_CARD_ANIMATE_RESTING} whileHover={INNER_CARD_HOVER} transition={INNER_CARD_TRANSITION_HOVER} className="md:col-span-2 bg-white border-2 border-black rounded-xl p-5">
                    <p className="text-sm font-semibold uppercase text-gray-500 mb-1">{t(data.contribution.labelKey)}</p>
                    <p className="text-4xl font-bold text-emerald-600">{data.contribution.currency || '$'}{data.contribution.amount.toLocaleString()}</p>
                    <p className="text-xs text-gray-500 mt-1">{t(data.contribution.subLabelKey)}</p>
                  </motion.div>
                )}
                {data.spotlightStory && (
                  <motion.div animate={INNER_CARD_ANIMATE_RESTING} whileHover={INNER_CARD_HOVER} transition={INNER_CARD_TRANSITION_HOVER} className={clsx("bg-white border-2 border-black rounded-xl p-5", data.contribution ? "md:col-span-3" : "md:col-span-5")}>
                    <div className="flex flex-col sm:flex-row gap-5 h-full">
                      {data.spotlightStory.imageUrl && (
                        <div className="sm:w-1/3 mb-3 sm:mb-0 shrink-0">
                          <img src={data.spotlightStory.imageUrl} alt={t(data.spotlightStory.titleKey)} className="w-full h-40 sm:h-full object-cover rounded-lg border-2 border-black" loading="lazy" />
                        </div>
                      )}
                      <div className="flex-grow flex flex-col">
                        <h4 className="text-md font-semibold text-gray-800 mb-1.5">{t(data.spotlightStory.titleKey)}</h4>
                        <p className="text-sm text-gray-700 mb-3 leading-relaxed flex-grow">{t(data.spotlightStory.descriptionKey)}</p>
                        
                        {/* --- THIS IS THE FIX --- */}
                        {/* Now using optional chaining `?.` on `data.spotlightStory` to safely access `linkToStory`. */}
                        {/* This prevents the "possibly 'undefined'" error at build time. */}
                        {data.spotlightStory.linkToStory && (
                          <div className="mt-auto"><Button textKey={data.spotlightStory.buttonKey} variant="ghost" size="sm" icon={<ExternalLink size={14} />} iconPosition='right' onClick={() => onAction('VIEW_IMPACT_STORY', { url: data.spotlightStory?.linkToStory })} className="text-violet-600 hover:text-violet-700" /></div>
                        )}
                      </div>
                    </div>
                  </motion.div>
                )}
              </div>
            </div>
          )}
          
          {data.learnMoreUrl && (
            <div className="text-center pt-4"><Button textKey={data.learnMoreButtonKey || 'socialImpactSection.learnMorePlatformImpactButton'} variant="ghost" size="lg" icon={<ExternalLink size={18} />} iconPosition="right" onClick={() => onAction('LEARN_MORE_PLATFORM_IMPACT', { url: data.learnMoreUrl })} className="text-violet-600 hover:text-violet-700 font-semibold" /></div>
          )}
        </div>
      </Card>
    );
  }
);

SocialImpactSection.displayName = "SocialImpactSection";